// Shared by the part-reference batch catalogs (`useMainImageResolution`, `useMinimumStockStatus`).
//
// #2819 — the maps these helpers produce MUST keep their object identity when their content has not
// changed. They feed the `columns` of the lists that use them, `columns` feeds `usePagination`
// (usePagination.jsx:91), which feeds `usePaginationMultiSelection`, which is a dependency of the
// effect that recomputes the "N items selected" count and calls `dataFetch`
// (SmartMateriaUITable.jsx:947-968). A brand-new map on every resolution closes a loop — re-resolve →
// new map → new columns → new pagination → count effect → re-resolve — that hammers the endpoints
// with no throttling while a row stays selected.

// Value equality of two map entries: the same primitive, or objects with the same fields (`===`).
export const sameEntry = (previous, next) => {
  if (previous === next) {
    return true
  }
  if (typeof previous !== 'object' || typeof next !== 'object' || previous === null || next === null) {
    return false
  }
  const previousKeys = Object.keys(previous)
  return previousKeys.length === Object.keys(next).length &&
    previousKeys.every((key) => previous[key] === next[key])
}

// The ids a query asks for: rows without a part reference (null, undefined or empty id) are dropped
// and duplicates are removed by normalized key (the numeric row id and the text key of an answer
// designate the same reference), so a list never issues one request per row.
export const requestedIds = (partReferenceIds) => {
  const seenKeys = new Set()
  return (partReferenceIds || []).filter((id) => {
    if (id === undefined || id === null || id === '' || seenKeys.has(String(id))) {
      return false
    }
    seenKeys.add(String(id))
    return true
  })
}

// A batch call answered correctly with a map. `apiPost` resolves `undefined` (it does not throw) when
// the connection is lost (Api_Functions.jsx:187-200): that is a failed query, not an empty answer —
// read as empty, RN2 would erase every requested entry.
export const isBatchAnswer = (answer) => typeof answer === 'object' && answer !== null && !Array.isArray(answer)

// Applies a batch response to a map, touching ONLY the requested ids: an id present in the response
// takes its value, a requested id absent from it is removed (e.g. its main image was unset), and any
// other entry is left as it is. Returns `previous` itself when nothing changes by value, so the map
// keeps its identity.
export const upsertRequestedEntries = (previous, requestedIds, response) => {
  let next = previous
  requestedIds.map(String).forEach((key) => {
    const present = Object.prototype.hasOwnProperty.call(response, key)
    if (present ? sameEntry(previous[key], response[key]) : !(key in previous)) {
      return
    }
    if (next === previous) {
      next = { ...previous }
    }
    if (present) {
      next[key] = response[key]
    } else {
      delete next[key]
    }
  })
  return next
}

// RN6 — "the query launched later wins", per id. A query is numbered when it is LAUNCHED (not when
// it answers); its answer only applies to the ids for which no query launched later has already
// been applied. The state is plain mutable data meant to live in a `useRef`: it must never cause a
// render by itself.
export const createQuerySequence = () => ({ launched: 0, appliedById: {} })

export const launchQuery = (querySequence) => {
  querySequence.launched += 1
  return querySequence.launched
}

// Returns the ids this answer may still apply to, and records them as applied by this query.
export const claimFreshIds = (querySequence, ids, queryNumber) => {
  const freshIds = ids.filter((id) => (querySequence.appliedById[String(id)] || 0) < queryNumber)
  freshIds.forEach((id) => { querySequence.appliedById[String(id)] = queryNumber })
  return freshIds
}
